import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Printer, X, Trash2, Truck, Loader2, Plus, Cloud,
} from "lucide-react";

function openPdfFromBase64(base64Data) {
  if (typeof base64Data === "string" && base64Data.startsWith("data:")) {
    base64Data = base64Data.split(",")[1];
  }
  const byteChars = atob(base64Data);
  const byteArray = new Uint8Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) byteArray[i] = byteChars.charCodeAt(i);
  const blob = new Blob([byteArray], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank");
}

function looksLikePdfBase64(base64Data) {
  try {
    if (typeof base64Data !== "string" || !base64Data.trim()) return false;
    let payload = base64Data.trim();
    if (payload.startsWith("data:")) {
      payload = payload.split(",")[1] || "";
    }
    if (!payload) return false;
    const head = atob(payload.slice(0, 64));
    return head.startsWith("%PDF");
  } catch {
    return false;
  }
}

async function printPdfFromBase64(base64Data) {
  if (typeof base64Data === "string" && base64Data.startsWith("data:")) {
    base64Data = base64Data.split(",")[1];
  }
  const byteChars = atob(base64Data);
  const byteArray = new Uint8Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) byteArray[i] = byteChars.charCodeAt(i);
  const blob = new Blob([byteArray], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  try {
    await printPdfFromUrl(url, { preferInline: true });
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 15_000);
  }
}

async function printPdfFromApiPath(path) {
  const blob = await api.get(path, { responseType: "blob" });
  if (!blob) throw new Error("PDF verisi alınamadı");
  const url = URL.createObjectURL(blob);
  try {
    await printPdfFromUrl(url, { preferInline: true });
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 15_000);
  }
}

function isIOS() {
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua);
}

function isSafariBrowser() {
  const ua = navigator.userAgent || "";
  const isWebKit = /AppleWebKit/i.test(ua);
  const isSafari = /Safari/i.test(ua);
  const isOther = /Chrome|CriOS|FxiOS|EdgiOS|OPR|SamsungBrowser/i.test(ua);
  return isWebKit && isSafari && !isOther;
}

async function tryPreviewWindowPrint(url, opts = {}) {
  const forceA6 = opts.forceA6 !== false;
  const popup = window.open("", "_blank", "width=900,height=700,noopener,noreferrer");
  if (!popup) return false;
  try {
    const frameSrc = String(url || "").trim();
    if (!frameSrc) return false;
    popup.document.open();
    popup.document.write(`<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Etiket Yazdır</title>
  <style>
    @page { size: ${forceA6 ? "105mm 148mm" : "auto"}; margin: 0; }
    html, body { margin: 0; padding: 0; width: 100%; height: 100%; background: #fff; overflow: hidden; }
    iframe { width: 100%; height: 100%; border: 0; }
  </style>
</head>
<body>
  <iframe id="labelFrame" src="${frameSrc.replace(/"/g, "&quot;")}"></iframe>
  <script>
    const frame = document.getElementById('labelFrame');
    const trigger = () => {
      setTimeout(() => {
        try { window.focus(); } catch {}
        try { window.print(); } catch {}
      }, 350);
    };
    frame.addEventListener('load', trigger, { once: true });
    setTimeout(trigger, 1200);
  </script>
</body>
</html>`);
    popup.document.close();
  } catch {
    try {
      popup.location.replace(url);
      setTimeout(() => {
        try { popup.focus(); } catch {}
        try { popup.print(); } catch {}
      }, 1000);
    } catch {
      return false;
    }
  }
  return true;
}

async function printPdfFromUrl(url, opts = {}) {
  if (!url) return;
  const forceA6 = opts.forceA6 !== false;
  const safari = isSafariBrowser();
  if (safari) {
    if (isIOS()) {
      // iOS Safari popup/print is highly restricted; navigate same tab as safest fallback.
      window.location.assign(url);
      return;
    }
    const ok = await tryPreviewWindowPrint(url, { forceA6 });
    if (!ok) window.location.assign(url);
    return;
  }
  const opened = await tryPreviewWindowPrint(url, { forceA6 });
  if (!opened) {
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    a.remove();
  }
}

const CODE128_PATTERNS = [
  "212222","222122","222221","121223","121322","131222","122213","122312","132212","221213","221312",
  "231212","112232","122132","122231","113222","123122","123221","223211","221132","221231","213212",
  "223112","312131","311222","321122","321221","312212","322112","322211","212123","212321","232121",
  "111323","131123","131321","112313","132113","132311","211313","231113","231311","112133","112331",
  "132131","113123","113321","133121","313121","211331","231131","213113","213311","213131","311123",
  "311321","331121","312113","312311","332111","314111","221411","431111","111224","111422","121124",
  "121421","141122","141221","112214","112412","122114","122411","142112","142211","241211","221114",
  "413111","241112","134111","111242","121142","121241","114212","124112","124211","411212","421112",
  "421211","212141","214121","412121","111143","111341","131141","114113","114311","411113","411311",
  "113141","114131","311141","411131","211412","211214","211232","2331112",
];

function normalizeDigits(v) {
  return String(v || "").replace(/\D/g, "");
}

function displayOrderNo(raw) {
  const s = String(raw || "").trim();
  if (!s) return "-";
  const normalized = s.replace(/^(TY-|WC-|HB-|AZ-|IDEFIX-|PAZARAMA-)/i, "").replace(/^#/, "");
  return normalized ? `#${normalized}` : s;
}

function pickItemBarcode(item) {
  const candidates = [
    item?.barcode,
    item?.product_barcode,
    item?.product_id,
    item?.sku,
  ];
  for (const c of candidates) {
    const raw = String(c || "").trim();
    if (!raw) continue;
    // Ignore marketplace stock codes like YNGC... and keep only numeric barcodes.
    if (/[a-zA-Z]/.test(raw)) continue;
    const digits = normalizeDigits(raw);
    if (digits.length >= 8) return digits;
  }
  return "";
}

function sanitizeProductName(name) {
  let text = String(name || "").trim();
  if (!text) return "-";
  // Remove marketplace stock codes like YNGC832855 from visible product title.
  text = text.replace(/\bYNGC[0-9A-Za-z]+\b/gi, "").trim();
  text = text.replace(/\s{2,}/g, " ").trim();
  // Cleanup dangling separators after stock-code removal.
  text = text.replace(/[-|_/.,:;]\s*$/g, "").trim();
  return text || "-";
}

function escapeHtml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function pickItemSku(item) {
  const candidates = [item?.sku, item?.barcode, item?.product_barcode, item?.product_id];
  for (const c of candidates) {
    const raw = String(c || "").trim();
    if (!raw) continue;
    if (/[a-zA-Z]/.test(raw)) continue;
    const digits = normalizeDigits(raw);
    if (digits.length >= 8) return digits;
  }
  return "-";
}

function formatMultiline(value) {
  return escapeHtml(String(value || "-")).replace(/\r?\n/g, "<br/>");
}

function code128InlineSvg(value) {
  let digits = normalizeDigits(value);
  if (!digits) return "";
  if (digits.length % 2 === 1) digits = `0${digits}`;
  const modules = [];
  const codes = [];
  for (let i = 0; i < digits.length; i += 2) codes.push(Number(digits.slice(i, i + 2)));
  const startCode = 105;
  let checksum = startCode;
  for (let i = 0; i < codes.length; i++) checksum += codes[i] * (i + 1);
  checksum %= 103;
  const seq = [startCode, ...codes, checksum, 106];
  for (const c of seq) modules.push(CODE128_PATTERNS[c] || "");
  modules.push("11");

  const modulePx = 2;
  const quiet = 10 * modulePx;
  const barH = 78;
  let x = quiet;
  let isBar = true;
  const rects = [];
  for (const pat of modules.join("")) {
    const w = Number(pat) * modulePx;
    if (isBar) rects.push(`<rect x="${x}" y="0" width="${w}" height="${barH}" fill="#111"/>`);
    x += w;
    isBar = !isBar;
  }
  const width = x + quiet;
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${barH}" viewBox="0 0 ${width} ${barH}" preserveAspectRatio="none">${rects.join("")}</svg>`;
}

function printHtml(labelHtml) {
  const html = labelHtml.replace(/<title>.*?<\/title>/i, "<title></title>");
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const w = window.open(url, "_blank", "width=860,height=900");
  if (!w) {
    URL.revokeObjectURL(url);
    return;
  }
  const cleanup = () => {
    try { URL.revokeObjectURL(url); } catch {}
  };
  const triggerPrint = () => {
    try { w.focus(); } catch {}
    setTimeout(() => {
      try { w.print(); } catch {}
      setTimeout(cleanup, 1500);
    }, 250);
  };
  w.addEventListener("load", triggerPrint, { once: true });
  setTimeout(triggerPrint, 1200);
  setTimeout(cleanup, 120000);
}

function trendyolFallbackLabelHtml(order) {
  const barcodeValue = normalizeDigits(order?.cargo_tracking || order?.trendyol_package_id || order?.channel_order_id);
  const barcodeSvg = code128InlineSvg(barcodeValue);
  const logoUrl = `${window.location.origin}/integrations/trendyol.svg`;
  const cssUrl = `${window.location.origin}/print-labels-v2.css?v=20260223-3`;
  const orderNo = String(order?.trendyol_order_number || order?.channel_order_id || "").replace(/^TY-/, "");
  const items = Array.isArray(order?.items) ? order.items : [];
  const itemRows = (items.length ? items : [{ product_name: "-", quantity: 1 }]).map((it) => {
    const sku = pickItemSku(it);
    const qty = Number(it?.quantity || 1);
    const productName = sanitizeProductName(it?.product_name || "-");
    return `<tr>
      <td class="p-qty"><div class="qty-val">${qty}</div></td>
      <td class="p-info">
        <div class="p-name">${escapeHtml(productName)}</div>
        <div class="p-sku">${escapeHtml(sku)}</div>
      </td>
    </tr>`;
  }).join("");

  return `<!doctype html><html><head><meta charset="utf-8"/><title>Trendyol Urunlu Etiket</title>
  <link rel="stylesheet" href="${cssUrl}"/></head><body>
  <div class="mobile-label-box">
    <div class="mobile-label-top">
      <div class="mobile-label-logo"><img src="${logoUrl}" alt="trendyol"/></div>
      <div class="mobile-label-bc">
        ${barcodeSvg ? `<div class="mobile-label-bcsvg">${barcodeSvg}</div>` : ""}
        <div class="mobile-label-bcnum">${escapeHtml(barcodeValue || "-")}</div>
      </div>
    </div>
    <table class="mobile-label-meta-table" cellspacing="0" cellpadding="0">
      <tr><td class="k">Sipariş</td><td>#${escapeHtml(orderNo || "-")} ${escapeHtml(order?.trendyol_package_id || "")}</td></tr>
      <tr><td class="k">Kargo</td><td>${escapeHtml(order?.cargo_company_name || "Trendyol Express / Gönderici Ödemeli")}</td></tr>
      <tr><td class="k">Alıcı</td><td>${escapeHtml(order?.customer_name || "-")}</td></tr>
      <tr><td class="k">Adres</td><td>${formatMultiline(order?.shipping_address || "-")}</td></tr>
    </table>
    <table class="mobile-label-products" cellspacing="0" cellpadding="0">
      ${itemRows}
    </table>
  </div></body></html>`;
}

function hepsiburadaProductLabelHtml(order) {
  const barcodeValue = normalizeDigits(order?.cargo_tracking || order?.hb_package_number || order?.channel_order_id);
  const barcodeSvg = code128InlineSvg(barcodeValue);
  const logoUrl = `${window.location.origin}/integrations/hepsiburada.svg`;
  const cssUrl = `${window.location.origin}/print-labels-v2.css?v=20260223-3`;
  const orderNo = String(order?.hb_order_number || order?.channel_order_id || "").replace(/^HB-/, "");
  const items = Array.isArray(order?.items) ? order.items : [];
  const itemRows = (items.length ? items : [{ product_name: "-", quantity: 1 }]).map((it) => {
    const sku = pickItemSku(it);
    const qty = Number(it?.quantity || 1);
    const productName = sanitizeProductName(it?.product_name || "-");
    return `<tr>
      <td class="p-qty"><div class="qty-val">${qty}</div></td>
      <td class="p-info">
        <div class="p-name">${escapeHtml(productName)}</div>
        <div class="p-sku">${escapeHtml(sku)}</div>
      </td>
    </tr>`;
  }).join("");

  return `<!doctype html><html><head><meta charset="utf-8"/><title>Hepsiburada Urunlu Etiket</title>
  <link rel="stylesheet" href="${cssUrl}"/></head><body>
  <div class="mobile-label-box">
    <div class="mobile-label-top">
      <div class="mobile-label-logo"><img src="${logoUrl}" alt="hepsiburada"/></div>
      <div class="mobile-label-bc">
        ${barcodeSvg ? `<div class="mobile-label-bcsvg">${barcodeSvg}</div>` : ""}
        <div class="mobile-label-bcnum">${escapeHtml(barcodeValue || "-")}</div>
      </div>
    </div>
    <table class="mobile-label-meta-table" cellspacing="0" cellpadding="0">
      <tr><td class="k">Sipariş</td><td>#${escapeHtml(orderNo || "-")} ${escapeHtml(order?.hb_package_number || "")}</td></tr>
      <tr><td class="k">Kargo</td><td>${escapeHtml(order?.cargo_company_name || "Hepsiburada")}</td></tr>
      <tr><td class="k">Alıcı</td><td>${escapeHtml(order?.customer_name || "-")}</td></tr>
      <tr><td class="k">Adres</td><td>${formatMultiline(order?.shipping_address || "-")}</td></tr>
    </table>
    <table class="mobile-label-products" cellspacing="0" cellpadding="0">
      ${itemRows}
    </table>
  </div></body></html>`;
}

function hepsiburadaFallbackLabelHtml(order) {
  const barcodeValue = normalizeDigits(order?.cargo_tracking || order?.hb_package_number || order?.channel_order_id);
  const barcodeSvg = code128InlineSvg(barcodeValue);
  const logoUrl = `${window.location.origin}/integrations/hepsiburada.svg`;
  const cssUrl = `${window.location.origin}/print-labels-v2.css?v=20260223-3`;
  const orderNo = String(order?.hb_order_number || order?.channel_order_id || "").replace(/^HB-/, "");
  const items = Array.isArray(order?.items) ? order.items : [];
  const itemRows = (items.length ? items : [{ product_name: "-", quantity: 1 }]).map((it) => {
    const sku = pickItemSku(it);
    const qty = Number(it?.quantity || 1);
    const productName = sanitizeProductName(it?.product_name || "-");
    return `<tr>
      <td class="p-qty"><div class="qty-val">${qty}</div></td>
      <td class="p-info">
        <div class="p-name">${escapeHtml(productName)}</div>
        <div class="p-sku">${escapeHtml(sku)}</div>
      </td>
    </tr>`;
  }).join("");

  return `<!doctype html><html><head><meta charset="utf-8"/><title>Hepsiburada Etiket</title>
  <link rel="stylesheet" href="${cssUrl}"/></head><body><div class="hb-fallback-box">
    <div class="hb-fallback-top">
      <div class="hb-fallback-logo"><img src="${logoUrl}" alt="hepsiburada"/></div>
      <div class="hb-fallback-bc">
        ${barcodeSvg ? `<div class="hb-fallback-bcsvg">${barcodeSvg}</div>` : ""}
        <div class="hb-fallback-bcnum">${escapeHtml(barcodeValue || "-")}</div>
      </div>
    </div>
    <table class="hb-fallback-meta-table">
      <tr><td class="k">Sipariş</td><td>#${escapeHtml(orderNo || "-")} ${escapeHtml(order?.hb_package_number || "")}</td></tr>
      <tr><td class="k">Kargo</td><td>${escapeHtml(order?.cargo_company_name || "Hepsiburada")}</td></tr>
      <tr><td class="k">Alıcı</td><td>${escapeHtml(order?.customer_name || "-")}</td></tr>
      <tr><td class="k">Adres</td><td>${escapeHtml(order?.shipping_address || "-")}</td></tr>
    </table>
    <table class="hb-fallback-products">${itemRows}</table>
  </div></body></html>`;
}

export default function CargoLabelSidebar({ open, onClose, selectedOrders, orders, onRefresh, provider = "hepsijet" }) {
  const [labelType, setLabelType] = useState("original");
  const [generating, setGenerating] = useState(false);
  const [deletingBarcode, setDeletingBarcode] = useState(null);
  const [creatingFor, setCreatingFor] = useState(null);
  const [printingBarcode, setPrintingBarcode] = useState(null);
  const [remotePrintingBarcode, setRemotePrintingBarcode] = useState(null);
  const [remoteGenerating, setRemoteGenerating] = useState(false);
  const [orderPatches, setOrderPatches] = useState({});
  const providerKey = String(provider || "").toLowerCase();
  const isTrendyol = providerKey === "trendyol";
  const isHepsiburada = providerKey === "hepsiburada";
  const isN11 = providerKey === "n11";
  const isPazarama = providerKey === "pazarama";
  const isIdefix = providerKey === "idefix";

  useEffect(() => {
    // For marketplace product-label flows, default to the custom "product" template.
    if (isTrendyol || isHepsiburada || isN11 || isPazarama) {
      setLabelType("product");
      return;
    }
    if (isIdefix) {
      setLabelType("original");
      return;
    }
    setLabelType("original");
  }, [isTrendyol, isHepsiburada, isN11, isIdefix, isPazarama, open]);

  useEffect(() => {
    if (!open) return undefined;
    const body = document.body;
    const scrollY = window.scrollY || window.pageYOffset || 0;
    const prev = {
      position: body.style.position,
      top: body.style.top,
      width: body.style.width,
      overflow: body.style.overflow,
    };
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.width = "100%";
    body.style.overflow = "hidden";
    return () => {
      body.style.position = prev.position;
      body.style.top = prev.top;
      body.style.width = prev.width;
      body.style.overflow = prev.overflow;
      window.scrollTo(0, scrollY);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setOrderPatches({});
  }, [open, providerKey]);

  if (!open) return null;

  const mergedOrders = (orders || []).map((o) => {
    const patch = orderPatches?.[o.id] || {};
    return { ...o, ...patch };
  });

  const getBarcodes = (order) => {
    const existing = Array.isArray(order?.cargo_barcodes)
      ? order.cargo_barcodes.filter((item) => String(item?.barcode || "").trim())
      : [];
    if (existing.length > 0) return existing;
    const tracking = String(order?.cargo_tracking || "").trim();
    if (tracking && !isTrendyol && !isHepsiburada && !isN11 && !isIdefix && !isPazarama) {
      return [{ barcode: tracking, created_at: order?.cargo_created_at || "" }];
    }
    return [];
  };

  const getMarketplaceCodes = (order) => {
    if (isN11) {
      const tracking = String(order?.n11_cargo_tracking || "").trim();
      return tracking ? [{ barcode: tracking, created_at: "" }] : [];
    }
    if (isHepsiburada) {
      const packageNo = String(order?.hb_package_number || order?.cargo_tracking || "").trim();
      return packageNo ? [{ barcode: packageNo, created_at: "" }] : [];
    }
    if (isTrendyol) {
      const packageNo = String(order?.trendyol_package_id || order?.channel_order_id || "").trim();
      return packageNo ? [{ barcode: packageNo, created_at: "" }] : [];
    }
    if (isIdefix) {
      const trackingNo = String(order?.idefix_cargo_tracking || order?.cargo_tracking || "").trim();
      return trackingNo ? [{ barcode: trackingNo, created_at: "" }] : [];
    }
    if (isPazarama) {
      const shipmentNo = String(order?.pazarama_cargo_tracking || order?.cargo_tracking || order?.pazarama_order_number || order?.channel_order_id || "").trim();
      return shipmentNo ? [{ barcode: shipmentNo, created_at: "" }] : [];
    }
    return [];
  };

  const getDisplayCodes = (order) => {
    const local = getBarcodes(order);
    if (local.length > 0) return local;
    return getMarketplaceCodes(order);
  };

  const orderHasPrintableLabel = (order) => {
    if (isTrendyol || isHepsiburada || isPazarama) return true;
    if (isIdefix) return getDisplayCodes(order).length > 0;
    return getDisplayCodes(order).length > 0;
  };

  const selectedOrdersList = selectedOrders.length > 0
    ? mergedOrders.filter(o => selectedOrders.includes(o.id))
    : mergedOrders.filter((o) => orderHasPrintableLabel(o));

  const ordersWithCargo = selectedOrdersList.filter(
    (o) => orderHasPrintableLabel(o)
  );
  const totalBarcodeCount = ordersWithCargo.reduce((sum, o) => sum + getBarcodes(o).length, 0);
  const totalPrintableCount = totalBarcodeCount;

  const handlePrintOriginalPDF = async () => {
    if (ordersWithCargo.length === 0) return;
    setGenerating(true);
    try {
      for (const order of ordersWithCargo) {
        const barcodes = getBarcodes(order);
        for (const bc of barcodes) {
          const result = await api.get(`/hepsijet/label/${bc.barcode}?format=PDF`);
          if (result.success && (result.file_url || result.data)) {
            if (result.file_url) await printPdfFromUrl(result.file_url);
            else await printPdfFromBase64(result.data);
          } else {
            toast.error(`Etiket alinamadi: ${bc.barcode}`);
          }
        }
      }
    } catch (err) {
      toast.error("PDF etiket hatasi: " + (err.message || ""));
    } finally {
      setGenerating(false);
    }
  };

  const handlePrintWithProducts = async () => {
    if (ordersWithCargo.length === 0) return;
    setGenerating(true);
    try {
      for (const order of ordersWithCargo) {
        const barcodes = getBarcodes(order);
        for (const bc of barcodes) {
          try {
            await printPdfFromApiPath(`/hepsijet/label-with-products/${order.id}/pdf?barcode=${encodeURIComponent(bc.barcode)}`);
          } catch {
            const result = await api.get(`/hepsijet/label-with-products/${order.id}?barcode=${encodeURIComponent(bc.barcode)}`);
            if (result.success && (result.file_url || result.data)) {
              if (result.file_url) await printPdfFromUrl(result.file_url);
              else await printPdfFromBase64(result.data);
            } else {
              toast.error(`Urunlu etiket alinamadi: ${order.channel_order_id}`);
            }
          }
        }
      }
    } catch (err) {
      toast.error("Etiket hatasi: " + (err.message || ""));
    } finally {
      setGenerating(false);
    }
  };

  const handlePrint = () => {
    if (isHepsiburada) {
      handlePrintHepsiburada();
      return;
    }
    if (isPazarama) {
      handlePrintPazarama();
      return;
    }
    if (isIdefix) {
      if (labelType === "original") {
        handlePrintIdefixOriginal();
      } else {
        handlePrintIdefix();
      }
      return;
    }
    if (isN11) {
      handlePrintN11();
      return;
    }
    if (labelType === "original") handlePrintOriginalPDF();
    else handlePrintWithProducts();
  };

  const handlePrintSingleHepsiburada = async (order, forcedBarcode = "") => {
    const selectedBarcode = normalizeDigits(forcedBarcode || order?.cargo_tracking || order?.hb_package_number || "");
    setPrintingBarcode(selectedBarcode || order.id);
    try {
      if (labelType === "product") {
        await printPdfFromApiPath(`/orders/${order.id}/product-label/pdf`);
        return;
      }
      let apiErrorDetail = "";
      try {
        const qs = selectedBarcode ? `?package_number=${encodeURIComponent(selectedBarcode)}` : "";
        const result = await api.get(`/hepsiburada/orders/${order.id}/label${qs}`);
            if (result?.file_url || result?.url) {
              await printPdfFromUrl(result.file_url || result.url, { forceA6: true });
              return;
            }
        const pdfCandidate = result?.data_base64 || result?.data;
        if (pdfCandidate && looksLikePdfBase64(pdfCandidate)) {
          await printPdfFromBase64(pdfCandidate);
          return;
        }
        if (result?.zpl) {
          apiErrorDetail = "API PDF yerine ZPL döndürdü";
        }
      } catch (err) {
        apiErrorDetail = String(err?.message || "");
      }
      printHtml(hepsiburadaFallbackLabelHtml(order));
      toast.warning(
        apiErrorDetail
          ? `Hepsiburada API etiketi yerine lokal etiket yazdırıldı. API hata: ${apiErrorDetail}`
          : "Hepsiburada API etiketi yerine lokal etiket yazdırıldı."
      );
    } catch (err) {
      toast.error("Hepsiburada etiket hatası: " + (err.message || ""));
    } finally {
      setPrintingBarcode(null);
    }
  };

  const handlePrintHepsiburada = async () => {
    const list = selectedOrdersList;
    if (!list.length) return;
    setGenerating(true);
    try {
      if (labelType === "product") {
        for (const order of list) {
          await handlePrintSingleHepsiburada(order);
        }
        return;
      }
      for (const order of list) {
        const barcodes = getBarcodes(order).map(b => normalizeDigits(b?.barcode || "")).filter(Boolean);
        if (barcodes.length) {
          for (const bc of barcodes) {
            await handlePrintSingleHepsiburada(order, bc);
          }
        } else {
          await handlePrintSingleHepsiburada(order);
        }
      }
    } finally {
      setGenerating(false);
    }
  };

  const handlePrintSingleTrendyol = async (order) => {
    setPrintingBarcode(order.id);
    try {
      await printPdfFromApiPath(`/orders/${order.id}/product-label/pdf`);
    } finally {
      setPrintingBarcode(null);
    }
  };

  const handlePrintTrendyol = async () => {
    const list = selectedOrdersList;
    if (!list.length) return;
    setGenerating(true);
    try {
      for (const order of list) {
        await handlePrintSingleTrendyol(order);
      }
    } finally {
      setGenerating(false);
    }
  };

  const handlePrintSingleN11 = async (order) => {
    setPrintingBarcode(order.id);
    try {
      await printPdfFromApiPath(`/orders/${order.id}/product-label/pdf`);
    } finally {
      setPrintingBarcode(null);
    }
  };

  const handlePrintN11 = async () => {
    const list = selectedOrdersList.filter((order) => orderHasPrintableLabel(order));
    if (!list.length) return;
    setGenerating(true);
    try {
      for (const order of list) {
        await handlePrintSingleN11(order);
      }
    } finally {
      setGenerating(false);
    }
  };

  const handlePrintSingleIdefix = async (order) => {
    setPrintingBarcode(order.id);
    try {
      await printPdfFromApiPath(`/orders/${order.id}/product-label/pdf`);
    } finally {
      setPrintingBarcode(null);
    }
  };

  const handlePrintIdefix = async () => {
    const list = selectedOrdersList.filter((order) => orderHasPrintableLabel(order));
    if (!list.length) return;
    setGenerating(true);
    try {
      for (const order of list) {
        await handlePrintSingleIdefix(order);
      }
    } finally {
      setGenerating(false);
    }
  };

  const handlePrintSingleIdefixOriginal = async (order, forcedBarcode = "") => {
    const selectedBarcode = String(
      forcedBarcode || order?.idefix_cargo_tracking || order?.cargo_tracking || ""
    ).trim();
    setPrintingBarcode(selectedBarcode || order.id);
    try {
      if (!selectedBarcode) {
        toast.error("Idefix için önce gerçek HepsiJet barkodunun oluşması gerekir");
        return;
      }
      await printPdfFromApiPath(`/orders/${order.id}/product-label/pdf?variant=shared`);
    } catch (err) {
      toast.error("Idefix etiket hatası: " + (err.message || ""));
    } finally {
      setPrintingBarcode(null);
    }
  };

  const handlePrintIdefixOriginal = async () => {
    const list = selectedOrdersList.filter((order) => orderHasPrintableLabel(order));
    if (!list.length) return;
    setGenerating(true);
    try {
      for (const order of list) {
        await handlePrintSingleIdefixOriginal(order);
      }
    } finally {
      setGenerating(false);
    }
  };

  const handlePrintSinglePazarama = async (order) => {
    setPrintingBarcode(order.id);
    try {
      await printPdfFromApiPath(`/orders/${order.id}/product-label/pdf`);
    } finally {
      setPrintingBarcode(null);
    }
  };

  const handlePrintPazarama = async () => {
    const list = selectedOrdersList.filter((order) => orderHasPrintableLabel(order));
    if (!list.length) return;
    setGenerating(true);
    try {
      for (const order of list) {
        await handlePrintSinglePazarama(order);
      }
    } finally {
      setGenerating(false);
    }
  };

  const handlePrintSingleBarcode = async (order, barcode) => {
    setPrintingBarcode(barcode);
    try {
      if (labelType === "product") {
        try {
          await printPdfFromApiPath(`/hepsijet/label-with-products/${order.id}/pdf?barcode=${encodeURIComponent(barcode)}`);
          return;
        } catch {
          // continue with JSON fallback
        }
      }
      const result = labelType === "original"
        ? await api.get(`/hepsijet/label/${barcode}?format=PDF`)
        : await api.get(`/hepsijet/label-with-products/${order.id}?barcode=${encodeURIComponent(barcode)}`);
      if (result.success && (result.file_url || result.data)) {
        if (result.file_url) await printPdfFromUrl(result.file_url);
        else await printPdfFromBase64(result.data);
      } else {
        toast.error(`Etiket alinamadi: ${barcode}`);
      }
    } catch (err) {
      toast.error("PDF etiket hatasi: " + (err.message || ""));
    } finally {
      setPrintingBarcode(null);
    }
  };

  const handleDeleteBarcode = async (order, barcode) => {
    setDeletingBarcode(barcode);
    try {
      const result = await api.delete(`/hepsijet/shipment/${barcode}`);
      if (result.status === "OK") {
        const existing = Array.isArray(order?.cargo_barcodes) ? order.cargo_barcodes : [];
        const remaining = existing.filter((x) => String(x?.barcode || "") !== String(barcode || ""));
        setOrderPatches((prev) => ({
          ...prev,
          [order.id]: {
            cargo_barcodes: remaining,
            cargo_tracking: remaining.length > 0 ? String(remaining[remaining.length - 1]?.barcode || "") : "",
          },
        }));
        toast.success(`${barcode} silindi`);
        await Promise.resolve(onRefresh?.());
      } else {
        toast.error(result.message || "Silinemedi");
      }
    } catch (err) {
      toast.error("Silme hatasi: " + (err.message || ""));
    } finally {
      setDeletingBarcode(null);
    }
  };

  const enqueueRemotePrint = async (order, barcode = "") => {
    const bc = String(barcode || "").trim();
    const key = bc || order.id;
    setRemotePrintingBarcode(key);
    try {
      const qs = bc ? `?barcode=${encodeURIComponent(bc)}` : "";
      const result = await api.post(`/print-jobs/enqueue/${order.id}${qs}`);
      if (result?.success) {
        toast.success("Uzaktan yazdırma kuyruğa eklendi");
      } else {
        toast.error(result?.message || "Uzaktan yazdırma kuyruğa eklenemedi");
      }
    } catch (err) {
      toast.error("Uzaktan yazdırma hatası: " + (err.message || ""));
    } finally {
      setRemotePrintingBarcode(null);
    }
  };

  const enqueueRemotePrintBulk = async () => {
    const list = selectedOrdersList;
    if (!list.length) return;
    setRemoteGenerating(true);
    try {
      for (const order of list) {
        if (isTrendyol || isHepsiburada || isN11 || isIdefix || isPazarama) {
          await enqueueRemotePrint(order, "");
          continue;
        }
        const barcodes = getBarcodes(order);
        if (barcodes.length === 0) {
          await enqueueRemotePrint(order, "");
          continue;
        }
        for (const bc of barcodes) {
          await enqueueRemotePrint(order, bc.barcode);
        }
      }
    } finally {
      setRemoteGenerating(false);
    }
  };

  const handleCreateNewBarcode = async (order) => {
    setCreatingFor(order.id);
    try {
      const result = await api.post(`/hepsijet/create-shipment/${order.id}`);
      if (result.success) {
        if (isN11) {
          const newTracking = String(result.barcode || order?.n11_cargo_tracking || "").trim();
          setOrderPatches((prev) => ({
            ...prev,
            [order.id]: {
              n11_cargo_tracking: newTracking,
            },
          }));
          toast.success(result.message || (newTracking ? `N11 kodu hazır: ${newTracking}` : "N11 etiketi hazırlandı"));
          await Promise.resolve(onRefresh?.());
          return;
        }
        const newBarcode = String(result.barcode || "").trim();
        const existing = Array.isArray(order?.cargo_barcodes) ? order.cargo_barcodes : [];
        const next = newBarcode
          ? [...existing, { barcode: newBarcode, created_at: new Date().toISOString() }]
          : existing;
        setOrderPatches((prev) => ({
          ...prev,
          [order.id]: {
            cargo_barcodes: next,
            cargo_tracking: newBarcode || String(order?.cargo_tracking || ""),
          },
        }));
        toast.success(`Yeni barkod: ${result.barcode}`);
        await Promise.resolve(onRefresh?.());
      } else {
        toast.error(result.message || "Olusturulamadi");
      }
    } catch (err) {
      toast.error("Hata: " + (err.message || ""));
    } finally {
      setCreatingFor(null);
    }
  };

  return createPortal((
    <>
      <div className="fixed inset-0 bg-black/20 z-[9996]" onClick={onClose} />
      <div className="fixed right-0 bottom-0 top-0 md:top-14 w-full md:w-[420px] bg-card border-l border-border shadow-2xl z-[9997] flex flex-col overscroll-none" data-testid="cargo-label-sidebar">
        <div className="flex items-center justify-between px-5 py-4 border-b border-border">
          <h2 className="text-base font-semibold text-foreground">Kargo Etiketi</h2>
          <button onClick={onClose} className="h-8 w-8 rounded-lg hover:bg-muted/60 inline-flex items-center justify-center" data-testid="close-cargo-sidebar">
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto overscroll-contain px-5 py-4 space-y-4">
          {/* Label Type */}
          <div className="space-y-2">
            <label className={cn("flex items-center gap-3 p-3 rounded-lg border transition-colors", (isTrendyol || isN11 || isPazarama) ? "border-border bg-muted/50 opacity-60 cursor-not-allowed" : (labelType === "original" ? "border-blue-300 bg-blue-50/50 cursor-pointer" : "border-border hover:bg-muted/50 cursor-pointer"))} data-testid="label-type-original">
              <input type="radio" name="lt" value="original" checked={labelType === "original"} onChange={() => setLabelType("original")} className="accent-blue-600" disabled={isTrendyol || isN11 || isPazarama} />
              <div>
                <span className="text-sm font-medium text-foreground/80">
                  {isTrendyol
                    ? "Orijinal Trendyol Etiketi"
                    : (isHepsiburada ? "Orijinal Hepsiburada Etiketi" : (isN11 ? "Orijinal N11 Etiketi" : (isIdefix ? "Orijinal HepsiJet Etiketi" : (isPazarama ? "Orijinal Pazarama Etiketi" : "Orijinal HepsiJet Etiketi"))))}
                </span>
                <p className="text-xs text-muted-foreground">
                  {isTrendyol
                    ? "Trendyol etiket API yanıtı tutarsız olduğu için bu seçenek pasif."
                    : (isN11 ? "N11 için platform içi özel etiket kullanılır." : (isIdefix ? "Idefix'in ürettiği ortak HepsiJet barkodu yazdırılır." : (isPazarama ? "Pazarama için platform barkodu ile özel etiket kullanılır." : "PDF formatinda orijinal etiket")))}
                </p>
              </div>
            </label>
            <label className={cn("flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors", labelType === "product" ? "border-blue-300 bg-blue-50/50" : "border-border hover:bg-muted/50")} data-testid="label-type-product">
              <input type="radio" name="lt" value="product" checked={labelType === "product"} onChange={() => setLabelType("product")} className="accent-blue-600" />
              <div>
                <span className="text-sm font-medium text-foreground/80">
                  {isTrendyol ? "Ürünlü Barkod Etiketi" : (isN11 ? "N11 Sipariş Etiketi" : (isIdefix ? "Idefix Sipariş Etiketi" : (isPazarama ? "Pazarama Sipariş Etiketi" : "Urunlu Etiket")))}
                </span>
                <p className="text-xs text-muted-foreground">
                  {isTrendyol
                    ? "Barkod numarasından ürünlü etiket oluşturulur"
                    : (isN11 ? "N11 sipariş kodu ile özel etiket PDF'i oluşturulur." : (isIdefix ? "Idefix barkodu ile ürünlü etiket PDF'i oluşturulur." : (isPazarama ? "Pazarama barkodu ile ürünlü etiket PDF'i oluşturulur." : "Orijinal etiket + urun listesi (PDF)")))}
                </p>
              </div>
            </label>
          </div>

          {/* Orders with barcodes */}
          <div className="space-y-3">
            <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Siparisler</h3>
            {selectedOrdersList.map(order => {
              const barcodes = getBarcodes(order);
              const displayCodes = getDisplayCodes(order);
              const printable = orderHasPrintableLabel(order);
              return (
                <div key={order.id} className="p-3 border border-border rounded-lg space-y-2" data-testid={`cargo-order-${order.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-800">{displayOrderNo(order.channel_order_id)}</p>
                      <p className="text-xs text-muted-foreground">{order.customer_name}</p>
                    </div>
                    <Badge variant="outline" className={cn("text-xs", printable ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200")}>
                      {isTrendyol
                        ? "Trendyol Etiketi"
                        : (isHepsiburada
                          ? "Hepsiburada Etiketi"
                          : (isN11
                            ? (printable ? "N11 Etiketi" : "N11 Hazırlanıyor")
                            : (isIdefix
                              ? "Idefix Etiketi"
                              : (isPazarama
                                ? "Pazarama Etiketi"
                                : (barcodes.length > 0 ? `${barcodes.length} Barkod` : "Barkod Yok")))))}
                    </Badge>
                  </div>

                  {/* List of barcodes */}
                  {!isTrendyol && !isHepsiburada && !isN11 && !isIdefix && !isPazarama && barcodes.length > 0 && (
                    <div className="space-y-1">
                      {barcodes.map((bc, idx) => (
                        <div key={bc.barcode} className="flex items-center justify-between gap-2 px-2 py-1.5 bg-muted/50 rounded text-xs" data-testid={`barcode-item-${bc.barcode}`}>
                          <span className="font-mono text-foreground/80 truncate">{bc.barcode}</span>
                          <div className="flex items-center gap-1">
                            <button
                              className="h-6 w-6 shrink-0 rounded hover:bg-blue-100 inline-flex items-center justify-center text-muted-foreground hover:text-blue-600 disabled:opacity-50"
                              onClick={() => handlePrintSingleBarcode(order, bc.barcode)}
                              disabled={printingBarcode === bc.barcode || generating}
                              data-testid={`print-barcode-${bc.barcode}`}
                              title="Bu etiketi yazdir"
                            >
                              {printingBarcode === bc.barcode ? <Loader2 className="h-3 w-3 animate-spin" /> : <Printer className="h-3 w-3" />}
                            </button>
                            <button
                              className="h-6 w-6 shrink-0 rounded hover:bg-emerald-100 inline-flex items-center justify-center text-muted-foreground hover:text-emerald-600 disabled:opacity-50"
                              onClick={() => enqueueRemotePrint(order, bc.barcode)}
                              disabled={remotePrintingBarcode === bc.barcode || remoteGenerating}
                              title="Uzaktan yazdır kuyruğuna ekle"
                            >
                              {remotePrintingBarcode === bc.barcode ? <Loader2 className="h-3 w-3 animate-spin" /> : <Cloud className="h-3 w-3" />}
                            </button>
                            <button
                              className="h-6 w-6 shrink-0 rounded hover:bg-red-100 inline-flex items-center justify-center text-muted-foreground hover:text-red-600 disabled:opacity-50"
                              onClick={() => handleDeleteBarcode(order, bc.barcode)}
                              disabled={deletingBarcode === bc.barcode}
                              data-testid={`delete-barcode-${bc.barcode}`}
                            >
                              {deletingBarcode === bc.barcode ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {(isN11 || isIdefix || isPazarama) && displayCodes.length > 0 && (
                    <div className="space-y-1">
                      {displayCodes.map((bc, idx) => (
                        <div key={`${bc.barcode}-${idx}`} className="flex items-center justify-between gap-2 px-2 py-1.5 bg-muted/50 rounded text-xs">
                          <span className="font-mono text-foreground/80 truncate">{bc.barcode}</span>
                          <div className="flex items-center gap-1">
                            <button
                              className="h-6 w-6 shrink-0 rounded hover:bg-blue-100 inline-flex items-center justify-center text-muted-foreground hover:text-blue-600 disabled:opacity-50"
                              onClick={() => {
                                if (isIdefix) {
                                  if (labelType === "original") {
                                    handlePrintSingleIdefixOriginal(order);
                                    return;
                                  }
                                  handlePrintSingleIdefix(order);
                                  return;
                                }
                                if (isPazarama) {
                                  handlePrintSinglePazarama(order);
                                  return;
                                }
                                handlePrintSingleN11(order);
                              }}
                              disabled={printingBarcode === order.id || generating}
                              title={isIdefix ? "Idefix etiketini yazdır" : (isPazarama ? "Pazarama etiketini yazdır" : "N11 etiketini yazdır")}
                            >
                              {printingBarcode === order.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Printer className="h-3 w-3" />}
                            </button>
                            <button
                              className="h-6 w-6 shrink-0 rounded hover:bg-emerald-100 inline-flex items-center justify-center text-muted-foreground hover:text-emerald-600 disabled:opacity-50"
                              onClick={() => enqueueRemotePrint(order, "")}
                              disabled={remotePrintingBarcode === order.id || remoteGenerating}
                              title="Uzaktan yazdır kuyruğuna ekle"
                            >
                              {remotePrintingBarcode === order.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Cloud className="h-3 w-3" />}
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Create new barcode button */}
                  {!isTrendyol && !isHepsiburada && !isN11 && !isIdefix && !isPazarama ? (
                  <button
                    className={cn(
                      "w-full h-7 text-xs rounded inline-flex items-center justify-center gap-1 disabled:opacity-50 transition-colors",
                      barcodes.length > 0
                        ? "border border-border hover:bg-muted/50 text-foreground/80"
                        : "bg-blue-600 text-white hover:bg-blue-700"
                    )}
                    onClick={() => handleCreateNewBarcode(order)}
                    disabled={creatingFor === order.id}
                    data-testid={`create-barcode-${order.id}`}
                  >
                    {creatingFor === order.id ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : barcodes.length > 0 ? (
                      <Plus className="h-3 w-3" />
                    ) : (
                      <Truck className="h-3 w-3" />
                    )}
                    {barcodes.length > 0 ? "Yeni Barkod Olustur" : "Barkod Olustur"}
                  </button>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        className="w-full h-7 text-xs rounded inline-flex items-center justify-center gap-1 border border-border hover:bg-muted/50 text-foreground/80"
                        onClick={() => {
                          if (isTrendyol) {
                            handlePrintSingleTrendyol(order);
                            return;
                          }
                          if (isHepsiburada) {
                            handlePrintSingleHepsiburada(order);
                            return;
                          }
                          if (isIdefix) {
                            if (!printable) return;
                            if (labelType === "original") {
                              handlePrintSingleIdefixOriginal(order);
                              return;
                            }
                            handlePrintSingleIdefix(order);
                            return;
                          }
                          if (isPazarama) {
                            handlePrintSinglePazarama(order);
                            return;
                          }
                          if (printable) {
                            handlePrintSingleN11(order);
                            return;
                          }
                          handleCreateNewBarcode(order);
                        }}
                        disabled={creatingFor === order.id || printingBarcode === order.id || generating || (isIdefix && !printable)}
                      >
                        {creatingFor === order.id || printingBarcode === order.id
                          ? <Loader2 className="h-3 w-3 animate-spin" />
                          : (printable ? <Printer className="h-3 w-3" /> : <Truck className="h-3 w-3" />)}
                        {isIdefix && !printable ? "Kargo Kodu Bekleniyor" : ((isN11 && !printable) ? "Etiketi Hazırla" : "Etiketi Yazdır")}
                      </button>
                      <button
                        className="w-full h-7 text-xs rounded inline-flex items-center justify-center gap-1 border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                        onClick={() => enqueueRemotePrint(order)}
                        disabled={!printable || remotePrintingBarcode === order.id || remoteGenerating}
                        title="Uzaktan yazdır kuyruğuna ekle"
                      >
                        {remotePrintingBarcode === order.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Cloud className="h-3 w-3" />}
                        Uzaktan Yazdır
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="px-5 py-4 border-t border-border">
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={isTrendyol ? handlePrintTrendyol : (isHepsiburada ? handlePrintHepsiburada : (isN11 ? handlePrintN11 : (isIdefix ? handlePrintIdefix : (isPazarama ? handlePrintPazarama : handlePrint))))}
              disabled={generating || ((isTrendyol || isHepsiburada || isN11 || isIdefix || isPazarama) ? selectedOrdersList.filter((order) => orderHasPrintableLabel(order)).length === 0 : ordersWithCargo.length === 0)}
              className="w-full h-10 rounded-lg bg-blue-600 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-2"
              data-testid="print-label-btn"
            >
              {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
              {generating ? "Hazirlaniyor..." : ((isTrendyol || isHepsiburada || isN11 || isIdefix || isPazarama) ? (selectedOrdersList.length > 1 ? "Tumunu Yazdir" : "Yazdir") : (totalPrintableCount > 1 ? "Tumunu Yazdir" : "Yazdir"))}
            </button>
            <button
              onClick={enqueueRemotePrintBulk}
              disabled={remoteGenerating || selectedOrdersList.filter((order) => orderHasPrintableLabel(order)).length === 0}
              className="w-full h-10 rounded-lg border border-emerald-200 bg-emerald-50 text-sm font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 transition-colors inline-flex items-center justify-center gap-2"
              title="Seçili siparişleri uzaktan yazdırma kuyruğuna ekle"
            >
              {remoteGenerating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Cloud className="h-4 w-4" />}
              {remoteGenerating ? "Kuyruklanıyor..." : "Uzaktan Yazdır"}
            </button>
          </div>
        </div>
      </div>
    </>
  ), document.body);
}
